import React, { useState } from 'react';
import { 
  Box, 
  Typography, 
  Paper, 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow,
  Button,
  Chip,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Alert
} from '@mui/material';
import { 
  Delete as DeleteIcon,
  CheckCircle as CheckCircleIcon 
} from '@mui/icons-material';
import { WaitlistEntry, WaitlistStatus } from '../../types';
import { waitlistApi } from '../../services/api';
import { useAuth } from '../../auth/AuthContext';
import { areIdsEqual } from '../../utils/typeUtils';
import { format } from 'date-fns';

interface WaitlistListProps {
  entries: WaitlistEntry[];
  onUpdate?: () => void;
  showResource?: boolean;
  showUser?: boolean;
}

const WaitlistList: React.FC<WaitlistListProps> = ({ 
  entries, 
  onUpdate,
  showResource = true,
  showUser = true
}) => {
  const { user } = useAuth();
  const [selectedEntry, setSelectedEntry] = useState<WaitlistEntry | null>(null);
  const [actionType, setActionType] = useState<'cancel' | 'convert' | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleCancel = (entry: WaitlistEntry) => {
    setSelectedEntry(entry);
    setActionType('cancel');
    setError(null);
  };

  const handleConvert = (entry: WaitlistEntry) => {
    setSelectedEntry(entry);
    setActionType('convert');
    setError(null);
  };

  const handleClose = () => {
    if (!loading) {
      setSelectedEntry(null);
      setActionType(null);
    }
  };

  const handleConfirm = async () => {
    if (!selectedEntry || !actionType) return;

    setLoading(true);
    setError(null);

    try {
      if (actionType === 'cancel') {
        await waitlistApi.cancel(selectedEntry.id);
      } else if (actionType === 'convert') {
        await waitlistApi.convertToReservation(selectedEntry.id);
      }

      if (onUpdate) {
        onUpdate();
      }
      
      setSelectedEntry(null);
      setActionType(null);
    } catch (err: any) {
      console.error(`Error ${actionType === 'cancel' ? 'cancelling' : 'converting'} waitlist entry:`, err);
      setError(err.response?.data?.error || `Failed to ${actionType} waitlist entry. Please try again later.`);
    } finally {
      setLoading(false);
    }
  };

  const getStatusChip = (status: WaitlistStatus) => {
    switch (status) {
      case 'pending':
        return <Chip label="Pending" color="primary" size="small" />;
      case 'notified':
        return <Chip label="Notified" color="info" size="small" />;
      case 'converted':
        return <Chip label="Converted" color="success" size="small" />;
      case 'cancelled':
        return <Chip label="Cancelled" color="error" size="small" />;
      default:
        return <Chip label={status} size="small" />;
    }
  };

  const formatDateTime = (dateString: string) => {
    try {
      const date = new Date(dateString);
      return format(date, 'PPP p'); // Format: "Apr 29, 2025, 12:00 PM"
    } catch (error) {
      return dateString;
    }
  };

  // Check if user can perform actions on an entry
  const canPerformAction = (entry: WaitlistEntry) => {
    return user?.role === 'admin' || areIdsEqual(entry.userId, user?.id);
  };

  // Check if entry can be converted to reservation
  const canConvert = (entry: WaitlistEntry) => {
    // Handle case where status might be capitalized or have different casing
    const status = String(entry.status).toLowerCase();
    console.log(`Entry ${entry.id} status: "${entry.status}", lowercase: "${status}"`);
    return status === 'pending' || status === 'notified';
  };

  if (entries.length === 0) {
    return (
      <Paper elevation={2} sx={{ p: 3, textAlign: 'center' }}>
        <Typography variant="body1">No waitlist entries found.</Typography>
      </Paper>
    );
  }

  return (
    <>
      <TableContainer component={Paper} elevation={2}>
        <Table>
          <TableHead>
            <TableRow>
              {showResource && <TableCell>Resource</TableCell>}
              {showUser && <TableCell>User</TableCell>}
              <TableCell>Start Time</TableCell>
              <TableCell>End Time</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Priority</TableCell>
              <TableCell>Created</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {entries.map((entry) => (
              <TableRow key={entry.id}>
                {showResource && (
                  <TableCell>
                    {entry.resource ? entry.resource.name : `Resource #${entry.resourceId}`}
                  </TableCell>
                )}
                {showUser && (
                  <TableCell>
                    {entry.user ? entry.user.name : `User #${entry.userId}`}
                  </TableCell>
                )}
                <TableCell>{formatDateTime(entry.startDate)}</TableCell>
                <TableCell>{formatDateTime(entry.endDate)}</TableCell>
                <TableCell>{getStatusChip(entry.status)}</TableCell>
                <TableCell>{entry.priority}</TableCell>
                <TableCell>{formatDateTime(entry.createdAt)}</TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    {canPerformAction(entry) && entry.status !== 'cancelled' && entry.status !== 'converted' && (
                      <Tooltip title="Cancel">
                        <IconButton 
                          size="small" 
                          color="error" 
                          onClick={() => handleCancel(entry)}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                    {/* Show convert button when appropriate */}
                    {(canPerformAction(entry) && String(entry.status).toLowerCase() === 'pending') && (
                      <Button
                        variant="contained"
                        size="small"
                        color="success"
                        onClick={() => handleConvert(entry)}
                        startIcon={<CheckCircleIcon />}
                      >
                        Convert to Reservation
                      </Button>
                    )}
                  </Box>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Confirmation Dialog */}
      <Dialog open={!!selectedEntry && !!actionType} onClose={handleClose}>
        <DialogTitle>
          {actionType === 'cancel' 
            ? 'Cancel Waitlist Entry' 
            : 'Convert to Reservation'}
        </DialogTitle>
        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          <Typography variant="body1">
            {actionType === 'cancel' 
              ? 'Are you sure you want to cancel this waitlist entry?' 
              : 'Are you sure you want to convert this waitlist entry to a reservation?'}
          </Typography>
          {actionType === 'convert' && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              This will only succeed if the resource is available for the requested time period.
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} disabled={loading}>
            Cancel
          </Button>
          <Button 
            onClick={handleConfirm} 
            color={actionType === 'cancel' ? 'error' : 'primary'} 
            variant="contained"
            disabled={loading}
          >
            {loading ? (
              <CircularProgress size={24} />
            ) : (
              actionType === 'cancel' ? 'Confirm Cancel' : 'Confirm Convert'
            )}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default WaitlistList;
